// deskCounts.js — which source may answer a number beside a Desk filter (2026-10-22).
//
// Live symptoms that day: the city dropdown read "Tbilisi (6406)", then 6122, then 5020 while the
// feed said "3,293 match"; Vake's tree count 309 beside "256 match" + "131 hidden"; "loaded 190,
// shown 90 · listings −49%". Each was a number taken from a different question than the one on
// screen. The decisions live here, pure, so node:test can pin them (deskCounts.test.mjs).
import { advFiltersActive } from './deskFeed.js'
import { DISTRICT_KEYS } from './districtFilter.js'

/**
 * City dropdown totals from a district count map.
 * `expanded` (0370/2477): each cluster sits at its own code AND every ancestor, so summing every code
 * in a city counted a Bagebi flat under Bagebi and again under Vake (~2× on Tbilisi). Expanded maps
 * are summed over the city's TOP codes only (no ancestor inside the same city). Un-expanded (loaded-row
 * tally, one code per cluster) still sums every code.
 * ⚠️ A disputed cluster's city share is not derivable from per-code totals (two top codes of one city
 * counted it twice before 3835 and count it nowhere since). `selectedTotal` (the landed search total
 * for the selected city) replaces the estimate where it exists.
 * `cityCounts` (core 3836, search_meta.city_counts): the distinct count the server tallied per city, keyed by
 * district.city (English) — what a city click returns. It wins wherever `cityOf(label)` can name the city; the
 * Σ above is the fallback only for an answer without it (Leads, a client-only cut, a withheld map). Since 3835 a
 * disputed cluster sits under NEITHER raion, so on Tbilisi the Σ now reads low instead of high.
 */
export function cityTotalsFrom({ cities = [], districts = [], counts = {}, expanded = false, inCity, selectedCity = null, selectedTotal = null, cityCounts = null, cityOf = null } = {}) {
  const m = new Map()
  const parentOf = new Map(districts.map(d => [d.code, d.parent_code || null]))
  for (const c of cities) {
    const key = cityCounts && cityOf ? cityOf(c) : null
    if (key) { m.set(c, cityCounts[key] || 0); continue }
    const codes = new Set()
    for (const d of districts) if (inCity(d, c)) codes.add(d.code)
    let s = 0
    for (const code of codes) {
      if (expanded) {
        let p = parentOf.get(code), hops = 0, covered = false
        while (p && hops++ < 16) { if (codes.has(p)) { covered = true; break } p = parentOf.get(p) }
        if (covered) continue
      }
      s += counts[code] || 0
    }
    m.set(c, s)
  }
  if (selectedCity && selectedCity !== 'all' && m.has(selectedCity) && Number.isFinite(selectedTotal)) m.set(selectedCity, selectedTotal)
  return m
}

/**
 * May the district tree fall back to the dc2: cache (the last UNFILTERED district answer for this
 * scope/agent/period/type/status)? Only when the question on screen is that question with district
 * keys aside: the district facet is a self-facet, so a city or a district pick does not change what
 * its numbers answer, but any other filter or committed text does. With a city picked the server
 * nulls district_counts; reading dc2 then drew unfiltered counts beside a filtered search.
 */
// `serverFilters` is serverAdvFilters (ADV_DEFAULTS-filled — advFiltersActive reads the blank bounds).
export function districtSeedAllowed(serverFilters, { committedText = false, leadsLens = false } = {}) {
  if (committedText || leadsLens) return false
  if (!serverFilters) return true
  const rest = { ...serverFilters }
  for (const k of DISTRICT_KEYS) rest[k] = []
  return !advFiltersActive(rest)
}

/**
 * Is the agent cut invisible to the server? searchWire sends agent_ids on scope 'all' only, and only
 * what reached serverAdvFilters (the role gate strips it there). Anything else is a loaded-rows cut.
 */
export function agentCutClientOnly(entryAgentFilter, serverFilters, scope = 'all') {
  if (!(entryAgentFilter || []).length) return false
  return scope !== 'all' || !((serverFilters && serverFilters.agentIds) || []).length
}

// Keys that are ONE question to the server: if any member moved, the whole group is re-judged.
const JOINT_KEYS = [['priceMin', 'priceMax', 'currency'], DISTRICT_KEYS]
const emptyish = v => v == null || v === '' || v === false || (Array.isArray(v) && v.length === 0)
const canon = v => (emptyish(v) ? null : Array.isArray(v) ? JSON.stringify([...v].map(String).sort()) : JSON.stringify(v))
const sameValue = (a, b) => canon(a) === canon(b)
// Keys the server may not have judged for some values (searchWire.js). A multi-pick of intents/kinds
// travels as intents=/kinds= since 2026-10-23 (core 3832), but a server without that change ignores the
// keys and answers wide, so the client keeps re-judging a multi-pick: on a current server every landed
// row passes and the re-check is a no-op; on an older one it is the only cut. Checked in deskCounts.test.mjs.
const NOT_ON_WIRE = { intents: v => (v || []).length > 1, kinds: v => (v || []).length > 1 }

/**
 * What the client must still judge on rows of a landed server-filtered answer.
 *   draft    — the classify's own filters (committed ⊎ live token overlay)
 *   sentView — the draft as it would be SENT (city codes injected, same transform as serverAdvFilters)
 *   landed   — the filters the landed answer was asked with; null = no server verdict on screen
 * Returns null when there is no landed verdict (classify everything as before), else
 * `{ recheck, cityJudged }`: `recheck` is the draft with every key the server judged with the same
 * value reset to its default; `cityJudged` says the district/city question is the server's too.
 */
export function serverVettedSpec({ draft, sentView, landed, defaults = {} } = {}) {
  if (!landed || !draft) return null
  const view = sentView || draft
  const groupOf = k => JOINT_KEYS.find(g => g.includes(k)) || [k]
  const judged = k => groupOf(k).every(g => sameValue(view[g], landed[g]) && !(NOT_ON_WIRE[g] && NOT_ON_WIRE[g](view[g])))
  const recheck = {}
  for (const k of Object.keys(draft)) {
    if (judged(k)) { if (k in defaults) recheck[k] = defaults[k] }
    else recheck[k] = draft[k]
  }
  return { recheck, cityJudged: DISTRICT_KEYS.every(k => sameValue(view[k], landed[k])) }
}

/**
 * The "−N%" / three-answer readout in SERVER mode. `before` must be the same question minus the
 * user's filters: same scope/period/status/type AND the same city (a city pick is not a cut the agent
 * made among listings). No comparable baseline → before: null, and the % / readout do not render.
 * `after` is the server total alone — subtracting a loaded-rows cut from a corpus count mixed units.
 * `unknown` is the server's own shelf count; client unknowns are page-scoped and would double count.
 */
export function serverReadout({ baseline = null, total = 0, serverUnknown = null } = {}) {
  return {
    before: Number.isFinite(baseline) && baseline > 0 ? baseline : null,
    after: Math.max(0, total || 0),
    unknown: Math.max(0, serverUnknown ?? 0),
  }
}

/** Key for a reduction baseline: the view axes plus the city. */
export const baselineKey = (viewKey, city) => `${viewKey}|${city || 'all'}`
